import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

export interface OpenDocument {
  uuid: string
  name: string
  clientId?: string
  projectId?: string
}

interface OpenDocumentsContextValue {
  tabs: OpenDocument[]
  openDocument: (doc: OpenDocument) => void
  closeDocument: (uuid: string) => void
  closeAll: () => void
}

const STORAGE_KEY = 'aerris.openDocuments'
const MAX_TABS = 12

const OpenDocumentsContext = createContext<OpenDocumentsContextValue | null>(null)

function readStored(): OpenDocument[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter(
      (x): x is OpenDocument =>
        x && typeof x.uuid === 'string' && typeof x.name === 'string'
    )
  } catch {
    return []
  }
}

export function OpenDocumentsProvider({ children }: { children: ReactNode }) {
  const [tabs, setTabs] = useState<OpenDocument[]>(readStored)

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(tabs))
    } catch {
      // ignore quota errors
    }
  }, [tabs])

  const openDocument = useCallback((doc: OpenDocument) => {
    setTabs((prev) => {
      const existing = prev.find((t) => t.uuid === doc.uuid)
      if (existing) {
        // Update metadata if changed (e.g. name fetched after open)
        if (
          existing.name === doc.name &&
          existing.clientId === doc.clientId &&
          existing.projectId === doc.projectId
        ) {
          return prev
        }
        return prev.map((t) => (t.uuid === doc.uuid ? { ...t, ...doc } : t))
      }
      const next = [...prev, doc]
      return next.length > MAX_TABS ? next.slice(next.length - MAX_TABS) : next
    })
  }, [])

  const closeDocument = useCallback((uuid: string) => {
    setTabs((prev) => prev.filter((t) => t.uuid !== uuid))
  }, [])

  const closeAll = useCallback(() => setTabs([]), [])

  const value = useMemo<OpenDocumentsContextValue>(
    () => ({ tabs, openDocument, closeDocument, closeAll }),
    [tabs, openDocument, closeDocument, closeAll]
  )

  return (
    <OpenDocumentsContext.Provider value={value}>
      {children}
    </OpenDocumentsContext.Provider>
  )
}

export function useOpenDocuments(): OpenDocumentsContextValue {
  const ctx = useContext(OpenDocumentsContext)
  if (!ctx) throw new Error('useOpenDocuments must be used within OpenDocumentsProvider')
  return ctx
}
